import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mic, Square, Sparkles, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { IDS } from "@/constants/testIds";

function ScoreBar({ label_cn, label_en, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-bold text-slate-600">
          <span className="cn mr-1">{label_cn}</span>· {label_en}
        </span>
        <span className="font-black text-slate-900">{value}</span>
      </div>
      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-rose-400 to-fuchsia-400"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
    </div>
  );
}

export default function Speaking() {
  const { currentProfile, isAuthed } = useAuth();
  const level = currentProfile?.level || "A2";

  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const loadPrompt = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/questions", {
        params: { skill: "speaking", level, limit: 1 },
      });
      setPrompt(data[0] || null);
      setTranscript(""); setFeedback(null); setAudioBlob(null);
      setAudioURL((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    } catch { toast.error("Could not load prompt."); }
    finally { setLoading(false); }
  }, [level]);

  useEffect(() => { loadPrompt(); }, [loadPrompt]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied. 请允许使用麦克风。");
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const submit = async () => {
    if (!audioBlob || !prompt) return;
    if (!isAuthed) {
      toast.error("Sign up to submit speaking for AI evaluation. 注册后使用。");
      return;
    }
    setBusy(true);
    try {
      // 1) upload audio for transcription
      const fd = new FormData();
      fd.append("audio", audioBlob, "recording.webm");
      const tRes = await api.post("/speaking/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const tx = tRes.data.transcript || "";
      setTranscript(tx);

      // 2) evaluate
      const eRes = await api.post("/speaking/submit", {
        prompt_id: prompt.id,
        prompt_en: prompt.prompt_en,
        transcript: tx,
        level: prompt.level,
      });
      setFeedback(eRes.data.feedback);
      toast.success("Feedback ready! 评语已生成。");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Evaluation failed.");
    } finally { setBusy(false); }
  };

  return (
    <AppLayout>
      <div data-testid={IDS.speaking.root} className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-100 to-rose-50 grid place-items-center">
            <Mic className="w-5 h-5 text-rose-600" />
          </span>
          <div>
            <span className="badge-soft mb-1">{level}</span>
            <BilingualText cn="口语练习" en="Speaking Practice" size="hero" />
          </div>
        </div>

        {loading ? (
          <div className="card-plush p-10 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : !prompt ? (
          <div className="card-plush p-10 text-center text-slate-400">No prompt available.</div>
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-plush p-6 sm:p-8 space-y-6"
          >
            <div className="bg-rose-50/60 border border-rose-100 rounded-2xl p-5">
              <BilingualText cn={prompt.prompt_cn} en={prompt.prompt_en} size="md" />
            </div>

            <div className="flex flex-wrap gap-3 items-center">
              {!recording ? (
                <Button
                  size="lg"
                  onClick={startRecording}
                  className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold"
                  data-testid={IDS.speaking.record}
                  disabled={busy}
                >
                  <Mic className="w-4 h-4 mr-2" />
                  <span className="cn mr-1">开始录音</span>Record
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={stopRecording}
                  className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                  data-testid={IDS.speaking.stop}
                >
                  <Square className="w-4 h-4 mr-2 fill-current" />
                  <span className="cn mr-1">停止</span>Stop
                </Button>
              )}

              {audioURL && (
                <div className="flex items-center gap-3">
                  <audio src={audioURL} controls className="max-w-xs" />
                </div>
              )}

              {audioBlob && !feedback && (
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full font-bold"
                  onClick={submit}
                  disabled={busy || !isAuthed}
                  data-testid={IDS.speaking.submit}
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                  )}
                  <span className="cn mr-1">AI 评价</span>Evaluate
                </Button>
              )}
              {!isAuthed && (
                <span className="text-xs text-slate-500 self-center">
                  <span className="cn">注册后可评估。</span> Sign up to evaluate.
                </span>
              )}
            </div>

            {transcript && (
              <div
                data-testid={IDS.speaking.transcript}
                className="rounded-2xl p-5 bg-slate-50 border border-slate-100"
              >
                <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                  Transcript · 转写
                </div>
                <div className="text-slate-800">{transcript}</div>
              </div>
            )}

            {feedback && (
              <motion.div
                data-testid={IDS.speaking.feedback}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-6 bg-gradient-to-br from-rose-50 to-fuchsia-50 border border-rose-100 space-y-5"
              >
                <div className="flex items-center justify-between">
                  <BilingualText cn="AI 评价" en="AI Evaluation" size="md" />
                  <div className="text-right">
                    <div className="serif text-4xl font-black text-slate-900">
                      {feedback.overall ?? 0}
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">
                      overall / 100
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <ScoreBar label_cn="发音" label_en="Pronunciation" value={feedback.pronunciation ?? 0} />
                  <ScoreBar label_cn="流利度" label_en="Fluency" value={feedback.fluency ?? 0} />
                  <ScoreBar label_cn="语法" label_en="Grammar" value={feedback.grammar ?? 0} />
                  <ScoreBar label_cn="词汇" label_en="Vocabulary" value={feedback.vocabulary ?? 0} />
                </div>

                <div>
                  <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">
                    Feedback · 评语
                  </div>
                  <div className="cn text-slate-900 leading-relaxed">
                    {feedback.feedback_cn}
                  </div>
                  <div className="mt-2 text-slate-700 leading-relaxed">
                    {feedback.feedback_en}
                  </div>
                </div>

                {(feedback.model_answer_en || feedback.model_answer_cn) && (
                  <div className="pt-4 border-t border-white/60">
                    <div className="text-xs font-black text-amber-700 uppercase tracking-widest mb-2">
                      Model answer · 参考答案
                    </div>
                    <div className="cn text-sm text-slate-800">{feedback.model_answer_cn}</div>
                    <div className="text-sm text-slate-600 italic mt-1">
                      &ldquo;{feedback.model_answer_en}&rdquo;
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            <Button
              variant="ghost"
              className="rounded-full font-bold"
              onClick={loadPrompt}
              disabled={busy}
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              <span className="cn mr-1">下一题</span>Try another prompt
            </Button>
          </motion.section>
        )}
      </div>
    </AppLayout>
  );
}
